import { Injectable } from '@angular/core';
import { CommonDataService } from '../common-data.service';
import { SolLevelCostCalculationService } from './sol-level-cost-calculation.service';
import { SolEstimateService } from '../estimate/sol-estimate.service';
import { LaborLevelwiseData, Material, MaterialLevelwiseData, MiscLevelwiseData, SubSystem, SystemComponent } from '../../models/dtos';
import { IndexDBService } from '../common/indexDB.service';
import { CONSTANTS } from '../../Constants/Constants';
import { CalculationLoaderService } from '../common/calculation-loader.service';

/**
 * Orchestration service that coordinates cost calculation and sell-price calculation.
 *
 * Flow:
 *  1. **Cost calculation** – delegates to `SolLevelCostCalculationService.calculateCostSummary()`
 *     which traverses the project hierarchy, computes material / labor / misc costs,
 *     and writes the totals into the shared `CommonDataService` state.
 *
 *  2. **Sell-price calculation** – delegates to `SolEstimateService.recalculateAllSections()`
 *     which reads cost data from `CommonDataService`, builds the `EstimateUiModel`,
 *     and syncs calculated values back to the DTOs.
 *
 * Consumers should call `calculateCostsAndSellPrices()` instead of invoking the
 * individual services directly. This guarantees the correct execution order
 * (costs before sell prices) and keeps the coordination in one place.
 */
@Injectable({
  providedIn: 'root'
})
export class SolCalculationsService {

  constructor(
    private readonly commonDataService: CommonDataService,
    private readonly solLevelCostCalculationService: SolLevelCostCalculationService,
    private readonly solEstimateService: SolEstimateService,
    private readonly indexDBService: IndexDBService,
    private readonly calculationLoader: CalculationLoaderService
  ) { }

  /**
   * Sets level-wise data collections required by the cost calculation service.
   * Must be called before `calculateCostsAndSellPrices()`.
   */
  setLevelWiseCollections(
    laborLevelwiseData: LaborLevelwiseData,
    miscLevelwiseData: MiscLevelwiseData,
    materialLevelwiseData: MaterialLevelwiseData
  ): void {
    this.solLevelCostCalculationService.setLevelWiseCollections(laborLevelwiseData, miscLevelwiseData, materialLevelwiseData);
  }

  /**
   * IMPORTANT: SET THE LEVEL-WISE COLLECTIONS (CALL setLevelWiseCollections) BEFORE CALLING THIS METHOD.
   * This is a pre-requisite for the cost calculation service to function correctly.
   *
   * Full calculation pipeline:
   *  1. Calculates all hierarchy costs (material, labor, misc).
   *  2. Patches values to the estimate UI model.
   *  3. Recalculates sell prices from the computed cost data.
   */
  calculateCostsAndSellPricesWithoutCollections() {
    this.solLevelCostCalculationService.recalculateCostSummary();
    this.solLevelCostCalculationService.clearLevelWiseCollections();
    this.recalculateEstimateTab();
  }

  /**
   * Full calculation pipeline – fetches level-wise collections from IndexDB first:
   *  0. Fetches material, labor, misc data from IndexDB.
   *  1. Calculates all hierarchy costs (material, labor, misc).
   *  2. Patches values to the estimate UI model.
   *  3. Recalculates sell prices from the computed cost data.
   */
  async calculateCostsAndSellPricesWithCollections() {
    await this.calculationLoader.run('cost-calculation', async scope => {
      const [materialLevelwiseData, laborLevelwiseData, miscLevelwiseData] = await Promise.all([
        this.indexDBService.getAllMaterialData(),
        this.indexDBService.getAllLaborData(),
        this.indexDBService.getAllMiscData()
      ]);

      // Set, calculate and clear stay in one synchronous block so a concurrent run cannot interleave on the shared collections.
      await scope.heavySync(() => {
        this.setLevelWiseCollections(laborLevelwiseData, miscLevelwiseData, materialLevelwiseData);
        this.solLevelCostCalculationService.recalculateCostSummary();
        this.solLevelCostCalculationService.clearLevelWiseCollections();
        this.recalculateEstimateTab();
      });
    });
  }

  /**
   * Applies an in-place cost change to every material of the level-wise collection, persists the
   * updated collection back to IndexDB and then runs the full cost + sell price pipeline.
   *
   * Callers that need to adjust material costs (e.g. product promotion discounts) should use this
   * instead of fetching / iterating / storing the collection themselves and afterwards calling
   * `calculateCostsAndSellPricesWithCollections()` – that would read the collection from IndexDB twice.
   *
   * @param applyCostChange Mutates a single material; invoked for every material in the hierarchy.
   */
  async applyMaterialCostChangeAndRecalculate(applyCostChange: (material: Material) => void): Promise<void> {
    await this.calculationLoader.run('material-cost-change', async scope => {
      const [materialLevelwiseData, laborLevelwiseData, miscLevelwiseData] = await Promise.all([
        this.indexDBService.getAllMaterialData(),
        this.indexDBService.getAllLaborData(),
        this.indexDBService.getAllMiscData()
      ]);

      if (!materialLevelwiseData?.materialsCollection) {
        return;
      }

      for (const materialData of Object.values(materialLevelwiseData.materialsCollection)) {
        this.applyCostChangeToChildren(materialData?.systemChildrenCollection, applyCostChange);
      }
      try {
        await this.indexDBService.storeMaterialData(materialLevelwiseData);
      } catch (error) {
        console.error('Failed to store updated material data.', error);
        return;
      }

      await scope.heavySync(() => {
        this.setLevelWiseCollections(laborLevelwiseData, miscLevelwiseData, materialLevelwiseData);
        this.solLevelCostCalculationService.recalculateCostSummary();
        this.solLevelCostCalculationService.clearLevelWiseCollections();
        this.recalculateEstimateTab();
      });
    });
  }

  private applyCostChangeToChildren(children: SystemComponent[], applyCostChange: (material: Material) => void): void {
    if (!children?.length) {
      return;
    }
    for (const child of children) {
      if (child.systemComponentNodeType === CONSTANTS.NODETYPE_SUBSYSTEM) {
        this.applyCostChangeToChildren((child as SubSystem).children, applyCostChange);
      } else {
        applyCostChange(child as Material);
      }
    }
  }

  /**
   * Subsidy-only recalculation. A subsidy just subtracts a scalar from one project-level material
   * category, so the hierarchy walk and the IndexDB read are skipped; falls back to the full
   * pipeline when no full calculation has run yet.
   */
  async recalculateSubsidy(): Promise<void> {
    if (this.solLevelCostCalculationService.recalculateSubsidy()) {
      this.recalculateEstimateTab();
      return;
    }
    await this.calculateCostsAndSellPricesWithCollections();
  }

  /**
   * Initial calculation on estimate open – uses the passed-in level-wise data
   * and runs the async (full) cost summary calculation.
   */
  async calculateAllOnEstimateOpen(materialLevelwiseData: MaterialLevelwiseData, laborLevelwiseData: LaborLevelwiseData, miscLevelwiseData: MiscLevelwiseData) {
    this.solLevelCostCalculationService.setLevelWiseCollections(laborLevelwiseData, miscLevelwiseData, materialLevelwiseData);
    await this.solLevelCostCalculationService.calculateCostSummary();
    this.solLevelCostCalculationService.clearLevelWiseCollections();
    this.recalculateEstimateTab();
  }

  /**
   * Sell-price-only recalculation (no cost recalculation).
   */
  calculateSellPrices() {
    // this.solEstimateService.recalculateAllSections();
    this.solEstimateService.notifyModelUpdated();
  }

  /**
   * Common post-cost-calculation pipeline:
   *  – Patches cost values into the estimate UI model.
   *  – Recalculates sell prices and notifies the estimate component.
   */
  private recalculateEstimateTab(): void {
    const estimateTab = this.commonDataService.getEstimateMetaData().estimateTab;
    this.solEstimateService.patchEstimateModelValues(estimateTab);
    this.calculateSellPrices();
  }

}
